import React, { useState, useEffect } from 'react';
import { mapsAPI } from '../../services/api';
import { X, Crown, User, UserPlus, Trash2, CheckCircle, AlertCircle, Users } from 'lucide-react';
import './CollaboratorsManager.css';

const CollaboratorsManager = ({ mapId, isOwner, onClose }) => {
    const [collaborators, setCollaborators] = useState([]);
    const [owner, setOwner] = useState(null);
    const [newEmail, setNewEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [addLoading, setAddLoading] = useState(false);
    const [removingEmail, setRemovingEmail] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchCollaborators();
    }, [mapId]);

    const fetchCollaborators = async () => {
        try {
            setLoading(true);
            const response = await mapsAPI.getCollaborators(mapId);
            setCollaborators(response.data.data.collaborators);
            setOwner(response.data.data.owner);
            setError(null);
        } catch (err) {
            setError('Failed to load collaborators');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCollaborator = async (e) => {
        e.preventDefault();
        if (!newEmail.trim() || !newEmail.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }
        try {
            setAddLoading(true);
            setError(null);
            setSuccessMessage('');
            await mapsAPI.addCollaborator(mapId, newEmail.trim());
            setSuccessMessage(`${newEmail} added as collaborator`);
            setNewEmail('');
            fetchCollaborators();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add collaborator');
        } finally {
            setAddLoading(false);
        }
    };

    const handleRemoveCollaborator = async (email) => {
        if (!window.confirm(`Remove ${email} as collaborator?`)) return;
        try {
            setRemovingEmail(email);
            setError(null);
            await mapsAPI.removeCollaborator(mapId, email);
            setSuccessMessage(`${email} removed`);
            fetchCollaborators();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError('Failed to remove collaborator');
        } finally {
            setRemovingEmail(null);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="cm-overlay" onClick={handleOverlayClick}>
            <div className="cm-modal">

                {/* Header */}
                <div className="cm-header">
                    <div className="cm-header-left">
                        <div className="cm-header-icon">
                            <Users size={16} />
                        </div>
                        <div>
                            <p className="cm-header-eyebrow">Map Access</p>
                            <h2 className="cm-header-title">Manage Collaborators</h2>
                        </div>
                    </div>
                    <button className="cm-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="cm-body">
                    {loading ? (
                        <div className="cm-loading">
                            <span className="cm-spinner cm-spinner--blue" />
                            <span>Loading collaborators…</span>
                        </div>
                    ) : (
                        <>
                            {/* Owner */}
                            <section className="cm-section">
                                <p className="cm-section-label">Owner</p>
                                <div className="cm-member-card cm-member-card--owner">
                                    <div className="cm-member-avatar cm-member-avatar--owner">
                                        <Crown size={14} />
                                    </div>
                                    <div className="cm-member-info">
                                        <p className="cm-member-email">{owner?.email}</p>
                                        <p className="cm-member-meta">Map Owner</p>
                                    </div>
                                    <span className="cm-owner-badge">Owner</span>
                                </div>
                            </section>

                            {/* Add collaborator */}
                            {isOwner && (
                                <section className="cm-section">
                                    <p className="cm-section-label">Add Collaborator</p>
                                    <form onSubmit={handleAddCollaborator} className="cm-add-form">
                                        <input
                                            type="email"
                                            placeholder="coworker@example.com"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            className="cm-input"
                                            disabled={addLoading}
                                        />
                                        <button type="submit" className="cm-add-btn" disabled={addLoading}>
                                            {addLoading ? (
                                                <span className="cm-spinner" />
                                            ) : (
                                                <UserPlus size={15} />
                                            )}
                                            {addLoading ? 'Adding…' : 'Add'}
                                        </button>
                                    </form>
                                    <p className="cm-helper">Enter a coworker's email to give them edit access to this map.</p>
                                </section>
                            )}

                            {/* Collaborators list */}
                            <section className="cm-section">
                                <p className="cm-section-label">
                                    Collaborators
                                    {collaborators.length > 0 && (
                                        <span className="cm-count">{collaborators.length}</span>
                                    )}
                                </p>

                                {collaborators.length === 0 ? (
                                    <div className="cm-empty">
                                        <div className="cm-empty-icon"><Users size={22} /></div>
                                        <p className="cm-empty-title">No collaborators yet</p>
                                        {isOwner && (
                                            <p className="cm-empty-sub">Add coworkers above to give them access.</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="cm-list">
                                        {collaborators.map((collab, i) => (
                                            <div key={i} className="cm-member-card">
                                                <div className="cm-member-avatar">
                                                    <User size={14} />
                                                </div>
                                                <div className="cm-member-info">
                                                    <p className="cm-member-email">{collab.email}</p>
                                                    <p className="cm-member-meta">
                                                        Added {new Date(collab.addedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </p>
                                                </div>
                                                {isOwner && (
                                                    <button
                                                        className="cm-remove-btn"
                                                        onClick={() => handleRemoveCollaborator(collab.email)}
                                                        disabled={removingEmail === collab.email}
                                                        title="Remove collaborator"
                                                    >
                                                        {removingEmail === collab.email
                                                            ? <span className="cm-spinner cm-spinner--sm" />
                                                            : <Trash2 size={14} />
                                                        }
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* Feedback messages */}
                            {error && (
                                <div className="cm-message cm-message--error">
                                    <AlertCircle size={15} />
                                    {error}
                                </div>
                            )}
                            {successMessage && (
                                <div className="cm-message cm-message--success">
                                    <CheckCircle size={15} />
                                    {successMessage}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="cm-footer">
                    <button className="cm-done-btn" onClick={onClose}>Done</button>
                </div>

            </div>
        </div>
    );
};

export default CollaboratorsManager;